/**
 * Утилиты для форматирования файлов: размер, расширение, цвета бейджей.
 * Используется в AttachmentsSection, EmailSection и др.
 */

export const formatBytes = (bytes?: number): string => {
  if (typeof bytes !== 'number' || isNaN(bytes)) return '\u2014';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export const getFileExt = (name?: string | null): string => {
  if (!name) return '';
  const i = name.lastIndexOf('.');
  return i === -1 ? '' : name.slice(i + 1).toLowerCase();
};

export const getExtBadgeColor = (ext: string): string => {
  switch (ext) {
    case 'pdf': return '#E11D48';
    case 'doc': case 'docx': return '#2563EB';
    case 'xls': case 'xlsx': case 'csv': return '#16A34A';
    case 'ppt': case 'pptx': return '#EA580C';
    case 'png': case 'jpg': case 'jpeg': case 'gif': case 'webp': return '#7C3AED';
    case 'svg': return '#9333EA';
    case 'zip': case 'rar': case '7z': return '#6B7280';
    case 'txt': return '#4B5563';
    case 'json': return '#CA8A04';
    case 'xml': case 'html': return '#EA580C';
    case 'mp3': case 'wav': return '#DB2777';
    case 'mp4': case 'mov': case 'avi': return '#8B5CF6';
    default: return '#64748B';
  }
};

/** Форматы, для которых доступен предпросмотр */
export const PREVIEW_FORMATS = new Set([
  'pdf',
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg',
  'txt', 'json', 'xml', 'html', 'csv',
]);

/** Форматы Office-документов (предпросмотр через Google Docs Viewer) */
export const OFFICE_FORMATS = new Set(['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']);

/** Форматы изображений */
export const IMAGE_FORMATS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg']);

export const canPreviewFormat = (format?: string | null): boolean => {
  if (!format) return false;
  const f = format.toLowerCase();
  return PREVIEW_FORMATS.has(f) || OFFICE_FORMATS.has(f);
};

export const isOfficeFormat = (format?: string | null): boolean => {
  if (!format) return false;
  return OFFICE_FORMATS.has(format.toLowerCase());
};

export const isImageFormat = (format?: string | null): boolean => {
  if (!format) return false;
  return IMAGE_FORMATS.has(format.toLowerCase());
};

/**
 * Загружает файл и возвращает blob URL для inline-рендера в <img>/<iframe>.
 * Нужен потому что backend отдаёт presigned URL с `Content-Disposition: attachment`,
 * и прямой src в iframe/img заставляет браузер скачивать вместо отображения.
 * Вызывающий обязан revokeObjectURL после использования.
 */
export async function fetchPreviewBlobUrl(
  url: string,
  contentType?: string | null,
): Promise<string> {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(`preview fetch failed: ${res.status}`);
  const data = await res.arrayBuffer();
  const type = contentType || res.headers.get('content-type') || 'application/octet-stream';
  return URL.createObjectURL(new Blob([data], { type }));
}

export const formatUploadDateTime = (iso?: string | null): string => {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso as string;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};
